/**
 * Public preview of the L'Chaim "This Week's Pillar" overview panel.
 * Uses the public day-content API — no auth required.
 * Route: /academy/lchaim/preview/:chord/:day   (add ?view=parent for the Coach's Companion styling)
 */
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { PillarOverviewPanel } from './LChaimDashboard';
import { ParentPillarPanel } from './LChaimCompanion';
import CoachBSpeak from '../components/CoachBSpeak';
import api from '../services/api';

const COLORS = { blueprintBg: '#0c2243', parchment: '#f1ead7', ink: '#1a2333', companionBg: '#1a1a2e' };

export default function LChaimPreview() {
  const { chord = '1', day = '1' } = useParams();
  const [sp] = useSearchParams();
  const isParent = sp.get('view') === 'parent';
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/academy/lchaim/content/${chord}/day/${day}?content_type=${isParent ? 'parent' : 'student'}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [chord, day, isParent]);

  const d = data?.day;

  if (isParent) {
    return (
      <div className="min-h-screen py-8 px-4" style={{ background: COLORS.companionBg }}>
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-6 text-white">
            <div className="text-2xl font-serif font-bold">Coach's Companion · Preview</div>
            <div className="text-[11px] tracking-[0.2em] uppercase text-white/50 mt-1">Chord {chord} · Day {day}</div>
          </div>
          {data?.week_intro && (
            <ParentPillarPanel intro={data.week_intro} week={data.week || 1} defaultOpen={data.is_pillar_start} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-10 px-4" style={{ background: COLORS.blueprintBg }}>
      <div className="max-w-lg mx-auto text-white">
        <div className="text-center mb-6">
          <div className="text-2xl font-black tracking-tight">L'CHAIM · Preview</div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mt-1">Chord {chord} · Day {day}</div>
        </div>

        {data?.week_intro && (
          <PillarOverviewPanel intro={data.week_intro} week={data.week || 1} defaultOpen={data.is_pillar_start} />
        )}

        {d && (
          <div className="mt-8 rounded-lg px-6 py-7" style={{ background: COLORS.parchment, color: COLORS.ink }} data-testid="preview-day-card">
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold opacity-60">Today's Word · Day {d.day}</div>
            <div className="text-xl font-black mt-1 mb-3">{d.title}</div>
            {/* Coach B reads the whole lesson aloud */}
            <div className="mb-4" data-testid="lchaim-preview-voice">
              <CoachBSpeak
                text={(() => {
                  const parts = [];
                  if (d.title) parts.push(`${d.title}.`);
                  if (d.the_word) parts.push(`The Word. ... ${d.the_word} ...`);
                  if (d.activity) parts.push(`The Charge. ... ${d.activity}`);
                  if (d.assignment && d.assignment !== d.activity) parts.push(`... Assignment. ... ${d.assignment}`);
                  return parts.join(' ').slice(0, 480);
                })()}
                label="Hear Coach B read this"
                variant="full"
                accent="#c99a3d"
                testId="lchaim-preview-coach-b"
              />
            </div>
            {d.the_word && <p className="text-sm leading-relaxed mb-4">{d.the_word}</p>}
            {d.activity && <p className="text-sm leading-relaxed mb-2"><span className="font-black uppercase text-[11px]">The Charge: </span>{d.activity}</p>}
            {d.assignment && d.assignment !== d.activity && <p className="text-sm leading-relaxed"><span className="font-black uppercase text-[11px]">Assignment: </span>{d.assignment}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
